import { useMutation, useQuery } from '@apollo/client/react';
import { type Editor } from '@tiptap/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import Header from '../Header';
import useOutletContext from '../useOutletContext';

import {
  GET_APPENDICES,
  GET_SELECTION_FIELDS,
  UPDATE_APPENDIX
} from './constants';
import LayoutTemplateSelector from './LayoutTemplateSelector';
import ReportTypeSelect from './ReportTypeSelect';
import type { Appendix, GetAppendices, GetLayoutTypes } from './types';
import { getLayoutFields } from './utils';

import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { fileToBase64 } from '@/lib/utils';

import DynamicFields from './DynamicFields';
import LayoutPreview from './LayoutPreview';

function EditAppendixForm({
  appendix,
  data
}: {
  appendix: Appendix;
  data: GetLayoutTypes;
}) {
  const navigate = useNavigate();
  const { id: opportunityId } = useParams<{ id: string }>();
  const isGeneral = appendix.appendixType === 'general';

  const [layoutTypeValue, setLayoutTypeValue] = useState(appendix.layoutType);
  const [reportTypeCodes, setReportTypeCodes] = useState<string[]>(
    appendix.reportTypeCodes ?? ['icb']
  );

  const textFieldRef = useRef<Editor | null>(null);
  const text2FieldRef = useRef<Editor | null>(null);
  const text3FieldRef = useRef<Editor | null>(null);
  const text4FieldRef = useRef<Editor | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (textFieldRef.current) {
        textFieldRef.current.commands.setContent(appendix.text || '');
      }
      if (text2FieldRef.current) {
        text2FieldRef.current.commands.setContent(appendix.text2 || '');
      }
      if (text3FieldRef.current) {
        text3FieldRef.current.commands.setContent(appendix.text3 || '');
      }
      if (text4FieldRef.current) {
        text4FieldRef.current.commands.setContent(appendix.text4 || '');
      }
    }, 100);
    return () => clearTimeout(timeout);
  }, [appendix.text, appendix.text2, appendix.text3, appendix.text4]);

  const [updateAppendix, { loading: isUpdating }] = useMutation(
    UPDATE_APPENDIX,
    {
      onCompleted: () => {
        toast.success('Appendix saved successfully');
        navigate(`/investments/direct/${opportunityId}/appendices`);
      },
      onError: () => {
        toast.error('Failed to save appendix');
      }
    }
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    try {
      const getFormText = (key: string) => {
        const value = formData.get(key);
        return typeof value === 'string' ? value : '';
      };

      const title = getFormText('title').trim();
      const dynamicFields = getLayoutFields(layoutTypeValue);

      const image1File = formData.get('image');
      const image2File = formData.get('image2');

      let imageBase64: string | undefined;
      if (
        dynamicFields.includes('image') &&
        image1File instanceof File &&
        image1File.size > 0
      ) {
        imageBase64 = await fileToBase64(image1File);
      }

      let image2Base64: string | undefined;
      if (
        dynamicFields.includes('image_2') &&
        image2File instanceof File &&
        image2File.size > 0
      ) {
        image2Base64 = await fileToBase64(image2File);
      }

      const getEditorHtml = (editor: Editor | null) => {
        if (!editor) return '';
        return editor.isEmpty ? '' : editor.getHTML();
      };

      const variables: Record<string, unknown> = {
        id: appendix.id
      };

      // General appendices cannot have title changed (backend enforces too)
      if (!isGeneral && title) variables.title = title;
      if (layoutTypeValue) variables.layoutType = layoutTypeValue;
      if (reportTypeCodes.length) variables.reportTypeCodes = reportTypeCodes;

      if (dynamicFields.includes('text'))
        variables.text = getEditorHtml(textFieldRef.current);
      if (dynamicFields.includes('text_2'))
        variables.text2 = getEditorHtml(text2FieldRef.current);
      if (dynamicFields.includes('text_3'))
        variables.text3 = getEditorHtml(text3FieldRef.current);
      if (dynamicFields.includes('text_4'))
        variables.text4 = getEditorHtml(text4FieldRef.current);

      if (dynamicFields.includes('text_header_1'))
        variables.textHeader1 = getFormText('textHeader1').trim();
      if (dynamicFields.includes('text_header_2'))
        variables.textHeader2 = getFormText('textHeader2').trim();
      if (dynamicFields.includes('text_header_3'))
        variables.textHeader3 = getFormText('textHeader3').trim();

      if (imageBase64) variables.image = imageBase64;
      if (image2Base64) variables.image2 = image2Base64;

      const { error } = await updateAppendix({ variables });

      if (error) {
        throw new Error('An error has occurred');
      }
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  return (
    <div className="flex gap-6">
      <div className="min-w-0 flex-1">
        <h2 className="mb-6 text-xl font-semibold">{appendix.title}</h2>

        <div className="bg-card border-border rounded-md border p-5">
          <form
            id="edit-appendix-form"
            noValidate
            onSubmit={event => void handleSubmit(event)}
          >
            <FieldGroup>
              <div className="flex gap-4">
                <Field className="flex-1">
                  <FieldLabel htmlFor="title-field">Title</FieldLabel>
                  <Input
                    defaultValue={appendix.title}
                    disabled={isGeneral}
                    id="title-field"
                    name="title"
                    placeholder="Enter Value"
                  />
                </Field>
                <Field className="flex-1">
                  <ReportTypeSelect
                    onValueChange={setReportTypeCodes}
                    value={reportTypeCodes}
                  />
                </Field>
              </div>

              <DynamicFields
                defaultImage={appendix.image}
                defaultImage2={appendix.image2}
                defaultTextHeader1={appendix.textHeader1}
                defaultTextHeader2={appendix.textHeader2}
                defaultTextHeader3={appendix.textHeader3}
                layoutTypeValue={layoutTypeValue}
                text2FieldRef={text2FieldRef}
                text3FieldRef={text3FieldRef}
                text4FieldRef={text4FieldRef}
                textFieldRef={textFieldRef}
              />

              {!isGeneral && (
                <LayoutTemplateSelector
                  data={data}
                  onValueChange={setLayoutTypeValue}
                  value={layoutTypeValue}
                />
              )}
            </FieldGroup>
          </form>

          <div className="mt-6 flex justify-end">
            <Button
              disabled={isUpdating}
              form="edit-appendix-form"
              type="submit"
            >
              {isUpdating && <Spinner />}
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      <div className="hidden w-[340px] shrink-0 lg:block">
        <p className="mb-4 text-lg font-medium">Slide Preview</p>
        <LayoutPreview
          data={data}
          layoutTypeValue={layoutTypeValue}
        />
      </div>
    </div>
  );
}

const EditAppendixPage = () => {
  const { appendixId, id } = useParams<{ appendixId: string; id: string }>();
  const { addBreadcrumb, additionalBreadcrumbs } = useOutletContext();

  const { data: layoutData, loading: loadingLayouts } =
    useQuery<GetLayoutTypes>(GET_SELECTION_FIELDS);

  const { data: appendicesData, loading: loadingAppendices } =
    useQuery<GetAppendices>(GET_APPENDICES, {
      skip: !id,
      variables: { dealId: id }
    });

  const appendix = appendicesData?.appendices.find(a => a.id === appendixId);

  useEffect(() => {
    if (!appendix || !addBreadcrumb) {
      return;
    }

    const alreadyAdded = additionalBreadcrumbs.some(
      crumb => crumb.label === appendix.title && !crumb.to
    );

    if (!alreadyAdded) {
      addBreadcrumb({ label: appendix.title });
    }
  }, [addBreadcrumb, additionalBreadcrumbs, appendix]);

  if (!id || !appendixId) {
    throw new Error();
  }

  if (loadingLayouts || loadingAppendices) {
    return (
      <>
        <Header />
        <div className="grid h-[calc(100%-64px)] place-content-center">
          <Spinner className="size-8 text-blue-500" />
        </div>
      </>
    );
  }

  if (!appendix || !layoutData) {
    return (
      <>
        <Header />
        <div className="grid h-[calc(100%-64px)] place-content-center">
          <p className="text-muted-foreground">Appendix not found.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="px-5 pt-5 pb-16">
        <EditAppendixForm
          appendix={appendix}
          data={layoutData}
        />
      </div>
    </>
  );
};

export default EditAppendixPage;
